import { MethodForModel, ModelName, ParamsForMethod, TellescopeQueryKey } from "../types";

/**
 * Parses a Tellescope query key tuple into a structured object.
 *
 * This utility function destructures the tuple format `[model, method, request?]`
 * used by TanStack Query into a more convenient object structure with named properties.
 *
 * @template Model - The Tellescope model name
 * @template Method - The method name for the model
 *
 * @param {TellescopeQueryKey<Model, Method>} queryKey - The query key tuple to parse
 * @returns An object containing the parsed `model`, `method`, and optional `request` parameters
 *
 * @example
 * ```ts
 * const queryKey = ["users", "getOne", { id: "user-123" }] as const;
 * const parsed = parseTellescopeQueryKey(queryKey);
 * // { model: "users", method: "getOne", request: { id: "user-123" } }
 * ```
 *
 * @example
 * ```ts
 * const queryKey = ["users", "getSome"] as const;
 * const parsed = parseTellescopeQueryKey(queryKey);
 * // { model: "users", method: "getSome", request: undefined }
 * ```
 */
export const parseTellescopeQueryKey = <Model extends ModelName, Method extends MethodForModel<Model>>([
  model,
  method,
  request,
]: TellescopeQueryKey<Model, Method>): {
  model: Model;
  method: Method;
  request?: Partial<ParamsForMethod<Model, Method>>;
} => ({
  model,
  method: method.endsWith("-count") ? (method.replace("-count", "") as Method) : (method as Method),
  request,
});
